import { useLayoutEffect, useState } from "react"

import { InvitationErrorCode } from "@/api/types"
import { ErrorMessage } from "@/components/error-message"
import { RequestNewInvitation } from "@/components/request-new-invitation"
import { getErrorMessage } from "@/lib/error-messages"

const RESEND_ELIGIBLE = new Set<InvitationErrorCode>([
  InvitationErrorCode.INVALID_TOKEN,
  InvitationErrorCode.INVITATION_EXPIRED,
])

const SENSITIVE_QUERY_KEYS = [
  "token_id",
  "resend_token",
  "resend_token_expires_at",
  "signee_email_hint",
] as const

interface ParamsSnapshot {
  errorCode: InvitationErrorCode | null
  errorMessage: string | null
  tokenId: string | null
  resendToken: string | null
  resendTokenExpiresAt: string | null
  signeeEmailHint: string | null
}

const snapshotParamsFromUrl = (): ParamsSnapshot => {
  const params = new URLSearchParams(window.location.search)
  return {
    errorCode: (params.get("error_code") ||
      params.get("code")) as InvitationErrorCode | null,
    errorMessage: params.get("error_message") || params.get("message"),
    tokenId: params.get("token_id"),
    resendToken: params.get("resend_token"),
    resendTokenExpiresAt: params.get("resend_token_expires_at"),
    signeeEmailHint: params.get("signee_email_hint"),
  }
}

const scrubSensitiveQueryParams = () => {
  const url = new URL(window.location.href)
  let mutated = false
  for (const key of SENSITIVE_QUERY_KEYS) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key)
      mutated = true
    }
  }
  if (mutated) {
    window.history.replaceState(window.history.state, "", url.toString())
  }
}

const InvitationError = () => {
  const [snapshot] = useState(snapshotParamsFromUrl)

  useLayoutEffect(() => {
    scrubSensitiveQueryParams()
  }, [])

  const canResend = Boolean(
    snapshot.errorCode &&
      RESEND_ELIGIBLE.has(snapshot.errorCode) &&
      snapshot.tokenId &&
      snapshot.resendToken &&
      snapshot.resendTokenExpiresAt &&
      snapshot.signeeEmailHint,
  )

  const { title, message, actionText } = getErrorMessage(
    snapshot.errorCode,
    snapshot.errorMessage,
  )

  const action = canResend ? (
    <RequestNewInvitation
      tokenId={snapshot.tokenId!}
      resendToken={snapshot.resendToken!}
      signeeEmailHint={snapshot.signeeEmailHint!}
    />
  ) : undefined

  return (
    <div className="mx-auto md:w-10/12 lg:w-8/12 xl:w-6/12">
      <ErrorMessage title={title} action={action}>
        <div className="flex flex-col gap-3">
          <p className="text-lg">{message}</p>
          <p className="text-muted-foreground">{actionText}</p>
        </div>
      </ErrorMessage>
    </div>
  )
}

export { InvitationError }
